/**
 * useStudentProgress — anonymous, no-login student progress tracked in localStorage.
 *
 * Reactive: any component calling this hook re-renders when progress changes,
 * even if the change came from another component (via the module-level event bus).
 *
 * SSR-safe: all localStorage access is guarded by typeof window checks + try/catch.
 * Dependency-free: no external state libraries; uses a tiny subscribe pattern.
 */

import { useState, useEffect, useCallback } from 'react';

// ── Persisted shape ────────────────────────────────────────────────────────────

export interface LastVisited {
  chapterId: string;
  board: string;
  grade: string;
  subject: string;
  slug: string;
  name: string;
  at: string; // ISO 8601 timestamp
}

export interface StudentProgress {
  lastVisited: LastVisited | null;
  completedChapters: string[]; // chapter ids
  quizScores: Record<string, { score: number; total: number }>;
}

const STORAGE_KEY = 'aces:progress:v1';

const DEFAULT: StudentProgress = {
  lastVisited: null,
  completedChapters: [],
  quizScores: {},
};

// ── Module-level mini-store ────────────────────────────────────────────────────
// All hook instances share this single in-memory state and subscriber list.

function readFromStorage(): StudentProgress {
  if (typeof window === 'undefined') return { ...DEFAULT };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT };
    const parsed = JSON.parse(raw) as Partial<StudentProgress>;
    return {
      lastVisited: parsed.lastVisited ?? null,
      completedChapters: Array.isArray(parsed.completedChapters)
        ? parsed.completedChapters
        : [],
      quizScores:
        parsed.quizScores && typeof parsed.quizScores === 'object'
          ? parsed.quizScores
          : {},
    };
  } catch {
    return { ...DEFAULT };
  }
}

function writeToStorage(state: StudentProgress): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // storage quota exceeded or private-browsing restriction — silently ignore
  }
}

type Subscriber = () => void;
const subscribers = new Set<Subscriber>();
let currentState: StudentProgress = readFromStorage();

function notify(): void {
  for (const sub of subscribers) sub();
}

function setState(updater: (prev: StudentProgress) => StudentProgress): void {
  currentState = updater(currentState);
  writeToStorage(currentState);
  notify();
}

// ── Public API ─────────────────────────────────────────────────────────────────

export interface StudentProgressAPI {
  // State
  lastVisited: LastVisited | null;
  completedChapters: string[];
  quizScores: Record<string, { score: number; total: number }>;
  // Read helpers
  isComplete: (chapterId: string) => boolean;
  getQuizScore: (chapterId: string) => { score: number; total: number } | undefined;
  getAll: () => StudentProgress;
  // Mutators
  recordVisit: (info: Omit<LastVisited, 'at'>) => void;
  markComplete: (chapterId: string) => void;
  toggleComplete: (chapterId: string) => void;
  setQuizScore: (chapterId: string, score: number, total: number) => void;
}

/**
 * React hook — returns reactive progress state + mutation methods.
 * Components re-render whenever any part of the progress changes.
 */
export function useStudentProgress(): StudentProgressAPI {
  const [state, setLocalState] = useState<StudentProgress>(() => readFromStorage());

  useEffect(() => {
    // Re-read on mount in case another tab wrote while we were unmounted
    setLocalState(readFromStorage());

    const sub: Subscriber = () => setLocalState({ ...currentState });
    subscribers.add(sub);
    return () => {
      subscribers.delete(sub);
    };
  }, []);

  const recordVisit = useCallback((info: Omit<LastVisited, 'at'>) => {
    setState((prev) => ({
      ...prev,
      lastVisited: { ...info, at: new Date().toISOString() },
    }));
  }, []);

  const markComplete = useCallback((chapterId: string) => {
    setState((prev) => {
      if (prev.completedChapters.includes(chapterId)) return prev;
      return { ...prev, completedChapters: [...prev.completedChapters, chapterId] };
    });
  }, []);

  const toggleComplete = useCallback((chapterId: string) => {
    setState((prev) => {
      const already = prev.completedChapters.includes(chapterId);
      return {
        ...prev,
        completedChapters: already
          ? prev.completedChapters.filter((id) => id !== chapterId)
          : [...prev.completedChapters, chapterId],
      };
    });
  }, []);

  const setQuizScore = useCallback((chapterId: string, score: number, total: number) => {
    setState((prev) => ({
      ...prev,
      quizScores: { ...prev.quizScores, [chapterId]: { score, total } },
    }));
  }, []);

  const isComplete = useCallback(
    (chapterId: string) => state.completedChapters.includes(chapterId),
    [state.completedChapters],
  );

  const getQuizScore = useCallback(
    (chapterId: string) => state.quizScores[chapterId],
    [state.quizScores],
  );

  const getAll = useCallback(() => ({ ...state }), [state]);

  return {
    lastVisited: state.lastVisited,
    completedChapters: state.completedChapters,
    quizScores: state.quizScores,
    isComplete,
    getQuizScore,
    getAll,
    recordVisit,
    markComplete,
    toggleComplete,
    setQuizScore,
  };
}
